import { useMemo, useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { Play } from 'lucide-react';
import { api, download } from '@/lib/api';
import type { DizaynLevel, ListResult, ScreenDef } from '@/lib/types';
import { DataGrid } from '@/grid/DataGrid';
import { dizaynColumns } from './columns';
import { dateShortcut, formatDate, parseDateInput } from '@/lib/format';
import { cn } from '@/lib/cn';
import { ReportMenu } from '@/reports/ReportMenu';
import { useWorkspace } from '@/shell/workspace';
import { RecordPanel } from './RecordPanel';
import { recordFormFor } from './custom/registry';
import { DateInput } from '@/ui/DateInput';
import { confirmDialog } from '@/ui/confirm';
import { toast } from 'sonner';
import { notifyDataChanged } from '@/grid/events';

/**
 * Dizayn tabanlı liste ekranı (Delphi Form72 / Dizayn kullanan özel liste formları).
 * Seviyeler (Level1..N) sekme olarak, her seviyenin SQL'i DIZAYN_KAYIT'tan, kolon davranışı Database_Yonetim'den gelir.
 */
export function DizaynScreen({ screen }: { screen: ScreenDef }) {
  const levels = screen.dizayn?.levels ?? [];
  const [active, setActive] = useState(String(levels[0]?.id ?? ''));
  const tabPos = screen.dizayn?.tabPosition ?? 'top';
  if (!levels.length) {
    return <div className="m-4 rounded border border-amber-300 bg-amber-50 p-4 text-amber-900">Geçerli Dizayn Bulunamadı. (Dizayn grubu: <b>{screen.settings?.dizaynGrup ?? screen.formTitle}</b>)</div>;
  }
  const body = (lv: DizaynLevel) => <LevelView key={lv.id} screen={screen} level={lv} />;
  if (levels.length === 1 || tabPos === 'none') return <div className="flex min-h-0 min-w-0 flex-1 flex-col p-2">{body(levels.find((l) => String(l.id) === active) ?? levels[0])}</div>;
  return (
    <Tabs.Root value={active} onValueChange={setActive} orientation={tabPos === 'left' ? 'vertical' : 'horizontal'}
      className={cn('flex min-h-0 min-w-0 flex-1 p-2', tabPos === 'left' ? 'flex-row gap-2' : 'flex-col')}>
      <Tabs.List className={cn('flex shrink-0', tabPos === 'left' ? 'w-48 flex-col gap-0.5' : 'gap-0.5 overflow-x-auto')}>
        {levels.map((l) => (
          <Tabs.Trigger key={l.id} value={String(l.id)}
            className={cn('rounded-t border border-b-0 border-transparent px-3 py-1.5 text-left text-[12.5px] muted data-[state=active]:b-border data-[state=active]:bg-[var(--panel)] data-[state=active]:font-medium data-[state=active]:text-[var(--text)]',
              tabPos === 'left' && 'rounded border-b')}>
            {l.caption}
          </Tabs.Trigger>
        ))}
      </Tabs.List>
      {levels.map((l) => (
        <Tabs.Content key={l.id} value={String(l.id)} className="flex min-h-0 min-w-0 flex-1 flex-col data-[state=inactive]:hidden" forceMount>
          {String(l.id) === active && body(l)}
        </Tabs.Content>
      ))}
    </Tabs.Root>
  );
}

function initialParams(level: DizaynLevel) {
  const o: Record<string, string> = {};
  for (const p of level.params) o[p.name] = p.type === 'date' ? (dateShortcut(p.default) ?? dateShortcut('bugün')!) : '';
  return o;
}

function LevelView({ screen, level }: { screen: ScreenDef; level: DizaynLevel }) {
  const ws = useWorkspace();
  const [params, setParams] = useState<Record<string, string>>(() => initialParams(level));
  const [applied, setApplied] = useState(params);
  const [source, setSource] = useState<ListResult['source']>(null);
  const [selected, setSelected] = useState<Record<string, any> | null>(null);
  const [detail, setDetail] = useState<Record<string, any> | null>(null);
  const editable = level.editable && !!source?.table;
  const rf = recordFormFor(screen);

  const fetch = async (q: any) => {
    const r = await api<ListResult>(`/screens/${screen.tag}/dizayn/${level.id}/list`, { body: { params: applied, query: q } });
    setSource(r.source ?? null);
    return r;
  };
  const editableCols = useMemo(() => (source ? new Set(Object.keys(source.map).map((k) => k.toLocaleUpperCase('tr-TR'))) : undefined), [source]);
  const keyOf = (row: Record<string, any>) => {
    const out: Record<string, any> = {};
    for (const k of source?.key ?? ['ID']) {
      const resultCol = Object.entries(source?.map ?? {}).find(([, v]) => v.toLocaleUpperCase('tr-TR') === k.toLocaleUpperCase('tr-TR'))?.[0] ?? k;
      out[k] = row[resultCol];
    }
    return out;
  };

  const openRecord = (row: Record<string, any> | null) => {
    if (rf) ws.open({ kind: 'custom', component: rf.component, title: row ? `${rf.title} · ${row[rf.titleField ?? 'ID'] ?? ''}` : `${rf.title} · Yeni`, props: { screen, id: row ? row[rf.idField ?? 'ID'] : null, row } }, { newInstance: !row });
    else setDetail(row);
  };

  /** Delphi liste formu BitBtn3Click: iki onay → hareket kontrolü → silme */
  const removeRecord = async (row: Record<string, any>) => {
    if (!(await confirmDialog('Silmek İstediğinizden Eminmisiniz ?', { danger: true, ok: 'Sil' }))) return;
    const c2 = screen.settings?.silme?.onay2;
    if (c2 && !(await confirmDialog(c2, { danger: true, ok: 'Evet, sil' }))) return;
    try {
      const id = row[rf?.idField ?? 'ID'];
      // Web_Screen.AYAR.silmeApi: belge iş akışlı silme (ör. fatura/iş emri: SP ve bağlı kayıt temizliği sunucuda)
      if (screen.settings?.silmeApi) await api(String(screen.settings.silmeApi).replace('{id}', encodeURIComponent(id)), { method: 'POST' });
      else await api(`/screens/${screen.tag}/kayit/${id}/delete`, { body: { row } });
      toast.success('Silindi');
      notifyDataChanged(screen.tag);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  return (
    <div className="flex min-h-0 min-w-0 flex-1 gap-2">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
        {level.params.length > 0 && (
          <div className="panel flex flex-wrap items-end gap-3 rounded px-3 py-2">
            {level.params.map((p) => (
              <label key={p.name} className="flex flex-col gap-1">
                <span className="text-xs muted">{p.label}</span>
                {p.type === 'date' ? (
                  <DateInput className="!w-40" value={params[p.name]} onChange={(v) => setParams((x) => ({ ...x, [p.name]: v ?? '' }))} />
                ) : (
                  <input className="inp !w-48" value={params[p.name] ?? ''} onChange={(e) => setParams((x) => ({ ...x, [p.name]: e.target.value }))}
                    onKeyDown={(e) => e.key === 'Enter' && setApplied(params)} />
                )}
              </label>
            ))}
            <button className="btn btn-primary" onClick={() => setApplied({ ...params })}><Play size={14} /> Listele</button>
            <span className="text-xs muted">{level.params.filter((p) => p.type === 'date').map((p) => formatDate(applied[p.name])).join(' – ')}</span>
          </div>
        )}
        <DataGrid
          queryKey={['dizayn', screen.tag, level.id, applied]}
          fetch={fetch}
          buildColumns={(meta) => dizaynColumns(meta, level.fields, { tag: screen.tag, editableCols: editable ? editableCols : undefined })}
          filterRow={level.filterBox}
          groupBox
          layoutKey={`dz:${screen.formTitle}:${level.name}`}
          onSelectionChange={setSelected}
          onRowOpen={editable ? undefined : (row) => openRecord(row)}
          editing={editable ? {
            canInsert: screen.rights.insert, canUpdate: screen.rights.update, canDelete: screen.rights.delete,
            onNewDefaults: level.hooks.new ? async (row) => (await api(`/screens/${screen.tag}/dizayn/${level.id}/new`, { body: { row } })).values : undefined,
            onSave: (row, original) => original
              ? api(`/screens/${screen.tag}/dizayn/${level.id}/rows`, { method: 'PUT', body: { row, key: keyOf(original), params: applied } })
              : api(`/screens/${screen.tag}/dizayn/${level.id}/rows`, { body: { row, params: applied } }),
            onDelete: (row) => api(`/screens/${screen.tag}/dizayn/${level.id}/delete`, { body: { key: keyOf(row), row } }),
          } : undefined}
          toolbarLeft={rf && !editable ? (
            <>
              {screen.settings?.yeniKayit !== false && <button className="btn" disabled={!screen.rights.insert} onClick={() => openRecord(null)} title="Yeni (F2)">+ Yeni</button>}
              <button className="btn" disabled={!selected} onClick={() => selected && openRecord(selected)}>Aç</button>
              {screen.settings?.silme && <button className="btn btn-danger" disabled={!selected || !screen.rights.delete} onClick={() => selected && removeRecord(selected)}>Sil</button>}
            </>
          ) : undefined}
          toolbarRight={<ReportMenu reports={screen.dizayn?.reports ?? []} row={selected} />}
          onExport={(q, cols) => download(`/export/dizayn/${screen.tag}/${level.id}`, { params: applied, query: q, columns: cols.map((c) => ({ key: c.key, caption: c.caption, mask: c.mask, sqlType: c.sqlType })) }, `${level.caption}.xlsx`)}
        />
      </div>
      {detail && <RecordPanel row={detail} fields={level.fields} title={level.caption} onClose={() => setDetail(null)} />}
    </div>
  );
}

export { parseDateInput };
